import queryString from 'query-string'

import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import filterFalsy from '@src/quince-site-common/src/utils/filterFalsy'

import { COLOR_PAGES_RENDERED_PATHS, VALENTINE_SLUGS, QUERY_KEYS } from './collection.constants'
import Collection, { SubCollection } from './domains/Collection'
import { EFilterKeys, SelectedFilter } from './domains/CollectionFilter'
import { ProductFeed } from './domains/ProductFeed'
import {
  ECollectionType,
  IProductFeedResponseForCollection,
} from './services/collectionService/CollectionService.types'
import { getCollectionCacheKey } from './utils'

export interface VariantList {
  product_id: number
  price: string
  labels: string
  variant_id: number
  index: number
}

// TODO: Cleanup this function usage
export const isFeedDynamic = (_slug: string) => false // DYNAMIC_FEEDS.includes(slug)

export function getBestSellerDataFromAPIProductFeed(
  subCollections: (SubCollection | CustomSection)[],
  productFeed: ProductFeed,
  merge?: boolean,
) {
  const outputSubCollections: (SubCollection | CustomSection)[] = []
  subCollections.forEach((subCollection, index) => {
    if (index === 0) {
      const updatedProductCards = [
        ...(merge ? ((subCollection as SubCollection).productCards ?? []) : []),
        ...(productFeed.productCards ?? []),
      ] as IProductCard[]
      outputSubCollections.push(
        new SubCollection({
          ...(subCollection as SubCollection),
          productCards: updatedProductCards,
        }),
      )
    } else {
      outputSubCollections.push(subCollection)
    }
  })
  return {
    subCollections: outputSubCollections,
    nextFeedURL: productFeed.nextURL,
  }
}

export const getNavigateTo = (selectedFilters: SelectedFilter, collectionPageSlug: string) => {
  if (COLOR_PAGES_RENDERED_PATHS.has(collectionPageSlug.slice(1))) {
    const appliedFilteredList = SelectedFilter.getSelectedValuesAsList(selectedFilters)
    const colorFilterSelectedValues = SelectedFilter.getSelectedFilterValuesByFilterName(
      selectedFilters,
      EFilterKeys.COLORS,
    )
    const isOnlySingleColorFilterSelected = appliedFilteredList.length === 1 && colorFilterSelectedValues[0]
    if (!isOnlySingleColorFilterSelected) return null
    return `${collectionPageSlug}/color/${colorFilterSelectedValues[0]?.toLowerCase() ?? ''}`
  }
  return null
}

export const getQueryParamsForSelectedFilters = (
  selectedFilters: SelectedFilter,
  sortBy: string,
  _collectionPageSlug = '',
) => {
  const formattedSelectedFiltersString = SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(selectedFilters)
  const queryParams = {
    ...queryString.parse(window.location.search),
    ...(sortBy ? { sortBy: sortBy !== SORT_BY.FEATURED.value ? sortBy : undefined } : {}),
    filter: formattedSelectedFiltersString || undefined,
  }

  return queryParams
}

export const getVariantsListFromCollectionNew = (collection: (SubCollection | CustomSection)[]): VariantList[] => {
  let index = 0
  const variantList: VariantList[] = []

  for (const subCollection of collection) {
    if (subCollection.typename !== 'CustomSection' && subCollection.productCards) {
      variantList.push(
        ...filterFalsy(
          subCollection.productCards.map(({ product }) => {
            if ('type' in product && product.type === PRODUCT_TYPES.BUNDLE) {
              return null
            }
            const variant1 = product.cardVariants[0]
            const price = variant1?.price?.salePrice
            if (!product.relatedIds.spid) return false
            return {
              product_id: Number(product.relatedIds.spid),
              price: `$${price}`,
              labels: product.title,
              variant_id: Number(variant1?.svids[0]),
              index: index++,
            }
          }),
        ),
      )
    }
  }
  return variantList
}

export const getTrackerUrlForSelectedFilters = (selectedFilters: SelectedFilter) => {
  let str = ''
  Object.keys(selectedFilters).forEach(sf => {
    const filterValues = selectedFilters[sf as keyof SelectedFilter]
    if (filterValues) {
      str += `__${sf}-${filterValues.join(',')}`
    }
  })
  return str
}

export const isValentineSlug = (slug: string) => {
  return VALENTINE_SLUGS.includes(slug)
}

const getProductKey = (productCard: IProductCard) => {
  const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
  return `${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`
}

function findFirstSubCollection(collectionPage: Collection) {
  return collectionPage.subCollections.find(sc => sc.typename === 'SubCollection') as SubCollection | undefined
}

function findFirstSubCollectionFromServer(subCollections: (SubCollection | CustomSection)[]) {
  return subCollections.find(sc => sc.typename === 'SubCollection') as SubCollection | undefined
}

function matchSubCollection(subCollectionA: SubCollection, subCollectionB: SubCollection) {
  return subCollectionA.title?.trim().toLowerCase() === subCollectionB.title?.trim().toLowerCase()
}

export const modifyFilteredCollectionOnProductFeed = (
  resp: IProductFeedResponseForCollection,
  newFilteredSubCollections: (SubCollection | CustomSection)[],
  page: number,
  isInitialCall: boolean,
  collectionPage: Collection,
  isOverrideServerSubCollection?: boolean,
) => {
  const serverSubCollection = isOverrideServerSubCollection
    ? findFirstSubCollectionFromServer(newFilteredSubCollections)
    : findFirstSubCollection(collectionPage)

  if (isOverrideServerSubCollection && isInitialCall) {
    const nonSubCollection = collectionPage.subCollections.filter(sc => sc.typename !== 'SubCollection')
    newFilteredSubCollections.splice(0, newFilteredSubCollections.length, ...resp.subCollections, ...nonSubCollection)
    return
  }

  if (
    // this is for the collection types which have only one subcollection
    [
      ECollectionType.PICKED_FOR_YOU,
      ECollectionType.TRENDING,
      ECollectionType.BESTSELLER,
      ECollectionType.ROOT,
      ECollectionType.NEW_ARRIVALS,
      ECollectionType.GIFT,
      ECollectionType.LAST_CHANCE,
    ].includes((resp.collectionType ?? '') as ECollectionType) ||
    isOverrideServerSubCollection
  ) {
    if (!newFilteredSubCollections[0] || newFilteredSubCollections[0].typename !== 'SubCollection') return

    const respSubCollection = resp.subCollections[0]

    if (!respSubCollection) return

    if (page === 0) {
      newFilteredSubCollections[0].productCards = respSubCollection.productCards ?? []
    } else {
      let newProductCards = respSubCollection.productCards ?? []
      if (serverSubCollection && matchSubCollection(serverSubCollection, respSubCollection)) {
        newProductCards = newProductCards.filter(productCard => {
          return serverSubCollection.productCards?.every(pc => getProductKey(pc) !== getProductKey(productCard))
        })
      }
      newFilteredSubCollections[0].productCards = [
        ...(newFilteredSubCollections[0].productCards ?? []),
        ...newProductCards,
      ]
    }

    if (!respSubCollection.productCards?.length) {
      // to avoid fetching the same data again
      newFilteredSubCollections[0].totalProducts = newFilteredSubCollections[0].productCards.length
    }
  } else if (
    [ECollectionType.STATIC, ECollectionType.ALL_NEW_ARRIVALS].includes(resp.collectionType as ECollectionType)
  ) {
    newFilteredSubCollections.forEach(subCollection => {
      if (subCollection.typename !== 'SubCollection') return

      const respSubCollection = resp.subCollections.find(
        sc => sc.title?.trim().toLowerCase() === subCollection.title?.trim().toLowerCase(),
      )

      if (!respSubCollection) return

      if (collectionPage.collectionType === 'static' && isInitialCall) {
        subCollection.productCards = [...(respSubCollection.productCards ?? [])]
      } else if (collectionPage.collectionType === 'static' && !isInitialCall) {
        let newProductCards = respSubCollection.productCards ?? []
        newProductCards = newProductCards.filter(productCard => {
          return subCollection.productCards?.every(pc => getProductKey(pc) !== getProductKey(productCard))
        })
        subCollection.productCards = [...(subCollection.productCards ?? []), ...newProductCards]
      } else {
        let newProductCards = respSubCollection.productCards ?? []
        if (serverSubCollection && matchSubCollection(serverSubCollection, respSubCollection)) {
          newProductCards = newProductCards.filter(productCard => {
            return serverSubCollection.productCards?.every(pc => getProductKey(pc) !== getProductKey(productCard))
          })
        }
        subCollection.productCards = [...(subCollection.productCards ?? []), ...newProductCards]
      }
      subCollection.totalProducts = respSubCollection.totalProducts

      if (!respSubCollection.productCards?.length) {
        // to avoid fetching the same data again
        subCollection.totalProducts = subCollection.productCards.length
      }
    })
  }
}

export function getProductTypeWithPageForStaticCollections(subCollections: SubCollection[], page: number) {
  const nextSubCollection = subCollections.find(sc => {
    return sc.totalProducts === undefined || (sc.productCards?.length ?? 0) < (sc.totalProducts ?? 0)
  })

  if (!nextSubCollection) {
    return { doNotFetch: true }
  }

  if (!nextSubCollection.totalProducts) return { page: 0, productType: nextSubCollection.title }

  return { page, productType: nextSubCollection.title }
}

export function splitCollectionSubSections(subSections: (SubCollection | CustomSection)[]) {
  const subCollections: SubCollection[] = []
  const customSections: CustomSection[] = []
  subSections.forEach(subSection => {
    if (subSection.typename === 'SubCollection') {
      subCollections.push(subSection)
    } else {
      customSections.push(subSection as CustomSection)
    }
  })
  return { subCollections, customSections }
}

export function isEmptyFeedResponse(feedResponse?: IProductFeedResponseForCollection): boolean {
  const totalProductsInResponse = feedResponse?.subCollections.reduce(
    (count, subCollection) => count + (subCollection.productCards?.length ?? 0),
    0,
  )

  return !feedResponse?.next_url && !totalProductsInResponse
}

export function handleGroupedPageFiltering(
  newFilteredSubCollections: (SubCollection | CustomSection)[],
  routerAsPath: string,
  collectionPage: Collection,
  resp: { total?: number },
  showGroupedPage: boolean,
  setTotalProductsCount: (count: number) => void,
) {
  if (showGroupedPage) {
    const pathParts = routerAsPath.split('/')
    const currentSubcollectionSlug = pathParts[pathParts.length - 1]
    const isOnMainCollectionPage = currentSubcollectionSlug === collectionPage.slug.split('/').pop()
    let totalProductsInCurrentSubcollection = 0

    // Only filter if we're on a specific subcollection page
    if (!isOnMainCollectionPage) {
      newFilteredSubCollections.forEach(subCollection => {
        if (subCollection.typename === 'SubCollection') {
          const subCollectionSlug = subCollection.slug?.split('/').pop()
          if (subCollectionSlug !== currentSubcollectionSlug) {
            // Clear products from other subcollections
            subCollection.productCards = []
            subCollection.totalProducts = 0
          } else {
            // Update total count for the current subcollection
            totalProductsInCurrentSubcollection = subCollection.totalProducts || 0
          }
        }
      })
      // Update total count to reflect only products in current subcollection
      setTotalProductsCount(totalProductsInCurrentSubcollection)
    } else if (resp.total !== undefined && !isNaN(resp.total)) {
      // On main collection page, use total from API
      setTotalProductsCount(resp.total)
    }
  } else if (resp.total !== undefined && !isNaN(resp.total)) {
    // Update total count from API response for non-grouped pages
    setTotalProductsCount(resp.total)
  }
}

// Constants for scroll behavior
const SCROLL_CONSTANTS = {
  SCROLL_DELAY_MS: 150,
  // Threshold to determine if filters are sticky (near top of viewport)
  STICKY_THRESHOLD_PX: 100,
  // Buffer space to position filters below viewport top when unsticking
  SCROLL_BUFFER_PX: 150,
  // Minimum scroll position to prevent over-scrolling to document top
  MIN_SCROLL_POSITION_PX: 50,
} as const

export function scrollToFiltersIfSticky(shouldScroll: boolean): void {
  if (!shouldScroll) return

  setTimeout(() => {
    try {
      const filterSection = document.getElementById('filter-section')
      if (!filterSection) return

      const rect = filterSection.getBoundingClientRect()
      // If filters are sticky (near top), scroll up to show them normally
      if (rect.top <= SCROLL_CONSTANTS.STICKY_THRESHOLD_PX) {
        window.scrollTo({
          // Calculate scroll position: current scroll + filter position - buffer
          // This moves the page so filters appear below viewport top (unsticky)
          // Math.max ensures we never scroll above minimum position (prevents over-scroll)
          top: Math.max(
            window.scrollY + rect.top - SCROLL_CONSTANTS.SCROLL_BUFFER_PX,
            SCROLL_CONSTANTS.MIN_SCROLL_POSITION_PX,
          ),
          behavior: 'smooth',
        })
      }
    } catch {
      //
    }
  }, SCROLL_CONSTANTS.SCROLL_DELAY_MS)
}

export const getDestinationCacheKey = (
  pathname: string,
  updatedWatchedQueryState: { filter?: string; sortBy?: string },
  navigateToOverwrite: string | null = null,
  currentSearchParams = '',
): string => {
  // If there's a specific navigation override (e.g., color pages), use that
  if (navigateToOverwrite) {
    // Remove leading slash to match cache key format
    const formattedPath = navigateToOverwrite.startsWith('/') ? navigateToOverwrite.slice(1) : navigateToOverwrite
    return getCollectionCacheKey(formattedPath, '')
  }

  // Remove leading slash from pathname to match cache key format (stored keys don't have leading slash)
  const formattedPathname = pathname.startsWith('/') ? pathname.slice(1) : pathname

  // Start with current search params to preserve experiment flags and other parameters
  const queryParams = new URLSearchParams(currentSearchParams)

  QUERY_KEYS.forEach(key => {
    const value = updatedWatchedQueryState[key]
    if (value) {
      queryParams.set(key, value)
    } else {
      queryParams.delete(key)
    }
  })

  const searchParams = queryParams.toString()
  return getCollectionCacheKey(formattedPathname, searchParams)
}
